/* ==========================================================================
   Optimize Minerals — one combined option, asked as two questions

   The MVP Bundle ships two products in a box and Shopify carries that as a
   single "Flavor" option with six values:

     Blood Orange OM + Citrus Blast Hydration+
     Blood Orange OM + Tropical Surge Hydration+
     Mixed Berry OM + Citrus Blast Hydration+
     ...

   Six tiles, each three lines long, every word but two repeated from the tile
   above it. The shopper is making two choices - which minerals, which
   hydration - and was being shown the multiplication table instead. Nothing is
   wrong with the data; the question was asked in the wrong shape.

   So this asks it in two: three minerals flavours, two hydration flavours,
   five controls instead of six paragraphs. The combination is recomposed on
   every click and the matching radio is clicked for real.

   It is a FACADE, like op-flavor-select. Dawn's <variant-radios> still owns
   variant selection, price, availability, the URL and everything Skio and the
   review app hang off; the original fieldset is hidden, not removed, and this
   clicks into it. One source of truth, and nothing downstream had to change.

   It is also entirely opt-out. Every value has to split on the separator into
   the same number of parts, and every pair of halves has to be a variant that
   exists, or the whole thing bails and leaves the original picker visible and
   untouched - which is the state this page was in before the file loaded.
   ========================================================================== */

(function () {
  'use strict';

  var SEP = ' + ';

  /* A fieldset whose every option value is a combination. Nothing else is
     touched: a product with a plain Flavor option never matches. */
  function findCombo() {
    var sets = document.querySelectorAll('.product-form__input');

    for (var i = 0; i < sets.length; i++) {
      var labels = sets[i].querySelectorAll('label[data-value]');
      if (labels.length < 4) continue;

      var ok = true;
      for (var j = 0; j < labels.length; j++) {
        if (labels[j].getAttribute('data-value').split(SEP).length !== 2) {
          ok = false;
          break;
        }
      }
      if (ok) return { fieldset: sets[i], labels: labels };
    }
    return null;
  }

  /* The word every value in a column ends with is the name of that column, not
     part of any one answer: "Blood Orange OM", "Mixed Berry OM", "Unflavored
     OM" is a list of three flavours of OM. Said once as a heading it stops
     being said three times in the options.

     Only a suffix shared by ALL of them qualifies. Two out of three is a
     coincidence, and stripping it would rename a flavour. */
  function commonSuffix(values) {
    var words = values[0].split(' ');
    var best = '';

    for (var take = 1; take < words.length; take++) {
      var candidate = words.slice(words.length - take).join(' ');
      var all = values.every(function (v) {
        return v.length > candidate.length &&
               v.slice(-candidate.length - 1) === ' ' + candidate;
      });
      if (!all) break;
      best = candidate;
    }
    return best;
  }

  function uniq(list) {
    var seen = {};
    var out = [];
    list.forEach(function (v) {
      if (!seen[v]) { seen[v] = 1; out.push(v); }
    });
    return out;
  }

  function build() {
    var found = findCombo();
    if (!found || found.fieldset.dataset.opCombo === '1') return;

    var labels = [].slice.call(found.labels);

    /* Every combination, indexed by its two halves, with the radio to click and
       the colour the original swatch was already showing for each half. */
    var byPair = {};
    var colsA = [];
    var colsB = [];
    var swatch = {};

    labels.forEach(function (label) {
      var parts = label.getAttribute('data-value').split(SEP);
      var input = document.getElementById(label.getAttribute('for'));
      if (!input) return;

      byPair[parts[0] + SEP + parts[1]] = input;
      colsA.push(parts[0]);
      colsB.push(parts[1]);

      var dots = label.querySelectorAll('.swatch-color__swatch');
      if (dots.length === 2) {
        if (!swatch[parts[0]]) swatch[parts[0]] = getComputedStyle(dots[0]).backgroundColor;
        if (!swatch[parts[1]]) swatch[parts[1]] = getComputedStyle(dots[1]).backgroundColor;
      }
    });

    colsA = uniq(colsA);
    colsB = uniq(colsB);
    if (colsA.length < 2 || colsB.length < 2) return;

    /* Every pair has to exist, or a shopper could pick two halves that are not
       a product. Shopify guarantees it for a full grid; this checks rather than
       assumes, because a merchant can delete a single variant. */
    for (var a = 0; a < colsA.length; a++) {
      for (var b = 0; b < colsB.length; b++) {
        if (!byPair[colsA[a] + SEP + colsB[b]]) return;
      }
    }

    found.fieldset.dataset.opCombo = '1';

    var checked = labels.filter(function (l) {
      var input = document.getElementById(l.getAttribute('for'));
      return input && input.checked;
    })[0] || labels[0];
    var chosen = checked.getAttribute('data-value').split(SEP);

    var wrap = document.createElement('div');
    wrap.className = 'op-combo';

    [
      { values: colsA, index: 0 },
      { values: colsB, index: 1 }
    ].forEach(function (group) {
      var suffix = commonSuffix(group.values);

      var field = document.createElement('div');
      field.className = 'op-combo__group';

      var heading = document.createElement('p');
      heading.className = 'op-combo__label';
      heading.textContent = suffix
        ? 'Choose your ' + suffix + ' flavor:'
        : 'Choose your flavor:';
      field.appendChild(heading);

      var row = document.createElement('div');
      row.className = 'op-combo__row';

      group.values.forEach(function (value) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'op-combo__opt';
        button.dataset.group = String(group.index);
        button.dataset.value = value;
        button.setAttribute('aria-pressed', value === chosen[group.index] ? 'true' : 'false');

        var dot = document.createElement('span');
        dot.className = 'op-combo__dot';
        dot.setAttribute('aria-hidden', 'true');
        if (swatch[value]) dot.style.background = swatch[value];
        button.appendChild(dot);

        var text = document.createElement('span');
        text.className = 'op-combo__name';
        text.textContent = suffix
          ? value.slice(0, value.length - suffix.length - 1)
          : value;
        button.appendChild(text);

        row.appendChild(button);
      });

      field.appendChild(row);
      wrap.appendChild(field);
    });

    found.fieldset.parentNode.insertBefore(wrap, found.fieldset);

    function paint() {
      wrap.querySelectorAll('.op-combo__opt').forEach(function (button) {
        var on = button.dataset.value === chosen[Number(button.dataset.group)];
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    wrap.addEventListener('click', function (e) {
      var button = e.target.closest('.op-combo__opt');
      if (!button) return;

      chosen[Number(button.dataset.group)] = button.dataset.value;
      paint();

      var input = byPair[chosen[0] + SEP + chosen[1]];
      /* click(), not checked = true: Dawn's handler is what updates the price,
         the URL and the form, and setting the property fires no event. */
      if (input && !input.checked) input.click();
    });

    /* The radios can also be changed from outside - a deep link with a variant
       id, or Dawn re-rendering this column - so the selection is re-read from
       whatever is checked rather than trusted to stay put. */
    document.addEventListener('change', function (e) {
      var radio = e.target;
      if (!radio || !radio.matches || !radio.matches('input[type="radio"]')) return;

      var label = document.querySelector('label[for="' + radio.id + '"][data-value]');
      if (!label) return;

      var parts = label.getAttribute('data-value').split(SEP);
      if (parts.length !== 2) return;
      chosen = parts;
      paint();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
  document.addEventListener('shopify:section:load', build);
})();
